<template>
	<div class="user-registration-layout display-flex flex-column flex-align-center">
		<div class="content-container display-flex flex-column flex-justify-space-between">
			<div class="display-flex flex-column flex-centerize width-100">
				<div class="title-text">This feature is powered by Ply</div>
				<div class="title-description-text mb-6">Ply lets you add features to your apps</div>
				<InstallablePreview class="width-100" :installableId="installableId" />
			</div>
			<div
				class="steps-container display-flex flex-row flex-align-center width-100 flex-justify-space-between"
				:class="{ 'flex-column': isMobile }"
			>
				<div
					v-for="(step, i) in installationSteps"
					:key="step.title"
					class="display-flex flex-column flex-align-center"
					:class="{ 'mt-3': isMobile, 'mb-1': isMobile }"
				>
					<SIcon :name="step.iconName" width="33" />
					<div class="step-title-text">{{ `${i + 1}. ${step.title}` }}</div>
				</div>
			</div>
			<div class="display-flex flex-column flex-centerize">
				<div
					class="card-container pt-6 pb-4 display-flex flex-column flex-centerize height-100"
					:class="{ 'narrow-padding': isNotSupportedMode, 'mobile-layout': isMobile }"
				>
					<div v-if="isNotSupportedMode" class="display-flex flex-centerize flex-column width-100">
						<div v-if="!isMobile" class="mini-title mb-3">{{ notSupportedTitle }}</div>
						<div class="display-flex flex-row width-100 mini-title-description flex-centerize">
							{{ notSupportedDescription }}
							<span class="display-flex flex-row" v-if="!isMobile">
								<a class="ml-1" :href="DOWNLOAD_CHROME_LINK" target="_blank">here</a>
								<div>)</div>
							</span>
						</div>
						<div class="success-message-text mt-6 mb-2 px-4" v-if="showSuccessSubscription">
							{{ successMessage }}
						</div>
						<div v-else class="flex-centerize flex-column">
							<div v-if="!isMobile" class="description-text mt-6 mb-2">let me know when it’s available for {{ browserName() }}.</div>
							<ClipLoader v-if="isLoading" color="#5F8FF1" size="18px" />

							<div v-else class="send-email-container display-flex flex-column">
								<SignupTextField
									label="Enter work email"
									iconName="email"
									:rules="emailRules"
									v-model="notSupportedBrowserEmail"
									type="email"
									data-cy="registration-username"
									single-line
									placeholder="mail@company.com"
								/>

								<AnemicButton @click="getEmailReminder" class="send-button">Continue</AnemicButton>
								<div class="terms-of-service mt-4 width-100 flex-centerize">
									By clicking continue you agree to our
									<a href="https://info.ply.io/privacy" target="_blank" class="terms-of-service ml-1">privacy policy </a>
								</div>
							</div>
						</div>
					</div>
					<v-form
						v-else
						ref="signupForm"
						v-model="isSignupFormValid"
						@submit.prevent="onContinue"
						class="user-input-box flex-centerize flex-column"
					>
						<div class="card-title mb-6">Create a free account to install</div>
						<AnemicButton class="google-login-button mb-5" @click="signInWithGoogle">
							<SIcon name="google-logo" class="mr-2" />
							Continue with Google
						</AnemicButton>

						<div @click="showEmailSignup = true" class="continue-with-email" v-if="!showEmailSignup">Or continue with email</div>
						<div v-else class="width-100">
							<div class="display-flex width-100 flex-row flex-align-center px-5">
								<div class="divider width-100"></div>
								<div class="divider-text mx-2">or</div>
								<div class="divider width-100"></div>
							</div>

							<SignupTextField
								label="Enter work email"
								iconName="email"
								:rules="emailRules"
								v-model="email"
								type="email"
								data-cy="registration-username"
								single-line
								placeholder="mail@company.com"
							/>

							<AnemicButton class="login-button width-100" type="submit" data-cy="registration-continue"> continue </AnemicButton>
						</div>
					</v-form>
				</div>
				<div v-if="!isNotSupportedMode" class="bottom-section mt-8">
					Already have an account?
					<a @click="navigateTo({ name: 'Login' })" class="primary-link-style">Log in</a>
				</div>
			</div>
		</div>
		<AppDialogManager />
	</div>
</template>

<script>
import ClipLoader from 'vue-spinner/src/ClipLoader.vue';
import { mapActions, mapGetters } from 'vuex';

import { FlowType, ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import SignupTextField from './userRegistration/components/SignupTextField';
import UserRegistrationMixin from './userRegistration/helpers/UserRegistrationMixin';
import UserRegistrationService from './userRegistration/helpers/UserRegistrationService';
import api from '@/api';
import InstallablePreview from '@/components/Project/InstallablePreview.vue';
import AppDialogManager from '@/components/dialogs/AppDialogManager/AppDialogManager';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getInstallable } from '@/store/modules/installables/getters';
import AnalyticsService from '@/utils/AnalyticsService';
import SignUpService from '@/utils/sign-up-service';
import UserAgentService from '@/utils/user-agent-service';

const isMobile = require('is-mobile');
const DOWNLOAD_CHROME_LINK = 'https://www.google.com/chrome/';

export default {
	name: 'InstallationSignup',
	mixins: [UserRegistrationMixin],
	components: {
		ClipLoader,
		SignupTextField,
		AppDialogManager,
		InstallablePreview,
	},
	props: {
		token: { type: String },
	},
	computed: {
		...mapGetters([getInstallable.fullPath]),
		installable() {
			return this[getInstallable.fullPath](this.localInstallableId) || {};
		},
		installableId() {
			return this.$route.params.installableId;
		},
		notSupportedTitle() {
			if (this.isMobile()) {
				return 'Enter your email to continue';
			} else {
				return 'Ply currently only works in Chrome';
			}
		},
		notSupportedDescription() {
			if (this.isMobile) {
				return 'Enter your email to continue';
			} else {
				return 'Open Ply from Chrome (Download chrome';
			}
		},
		successMessage() {
			if (this.isMobile) {
				return 'We have sent you an email reminder, to use Ply in your desktop';
			} else {
				return `We'll let you know when our extension will be available in ${this.browserName()} browser`;
			}
		},
		isNotSupportedMode() {
			return !UserAgentService.isUsingChromium() || this.isMobile;
		},
		isMobile() {
			return isMobile();
		},
		installableIdFromRoute() {
			const returnToQuery = this.$route.query?.return_to || '';
			const returnToArr = returnToQuery.split('/');
			return returnToArr.slice(-2)[0];
		},
	},
	methods: {
		...mapActions(['loginWithGoogleToken']),
		browserName() {
			return UserAgentService.getBrowserName();
		},
		async getEmailReminder() {
			if (this.isMobile) {
				return this.getDesktopReminder();
			}
			this.isLoading = true;
			try {
				await api.signForExtensionUpdate(this.notSupportedBrowserEmail, UserAgentService.getBrowserName());
				this.showSuccessSubscription = true;
			} catch (err) {
				console.log('signForExtensionUpdate ~ err', err);
			} finally {
				this.isLoading = false;
			}
		},
		openGmailWorkspaceSignupDialog(idToken) {
			AppDialogManagerService.openGmailWorkspaceSignupDialog({
				onReplace: () => {
					UserRegistrationService.initializeGoogleAuth();
					this.signInWithGoogle();
				},
				onContinue: () => this.continueWithGmail(idToken),
			});
		},
		getDesktopReminder() {
			const utms = SignUpService.getReferralAndUtmParams(this.$route.query);
			AnalyticsService.updateUserProps({ email: this.notSupportedBrowserEmail, ...utms, installableId: this.installableId });
			AnalyticsService.sendEvent('Mobile Installable Link Sent', {
				installableId: this.installableId,
				isMobile: this.isMobile,
				email: this.notSupportedBrowserEmail,
				/* Pass along utm as well */
				...utms,
			});
			api.sendContinueInstallationSignupMail({
				email: this.notSupportedBrowserEmail,
				installable_id: this.installableId,
				utms,
			});
			this.$router.push({
				name: 'MobileInstallation',
				query: { installableId: this.installableId },
				params: { installableId: this.installableId },
			});
		},
		onContinue() {
			this.$refs.signupForm.validate();
			if (this.isSignupFormValid) {
				this.goToNextPage({ userEmail: this.email, isInstallable: true, installableId: this.installableId });
			} else {
				console.log('Please agree to terms of use');
			}
		},
		isEmailRegularGmail(email) {
			return email.includes('@gmail.com');
		},
		isGmailWorkspaceRequiredByInstallable() {
			return (
				this.installable.workflow_type === FlowType.Experience &&
				this.installable.feature_context_service?.service_slug === ServiceSlugs.GmailWorkspace
			);
		},
		async signInWithGoogle() {
			const googleUser = await UserRegistrationService.signInWithGoogle(true);
			const userEmail = googleUser.getBasicProfile().getEmail();
			const idToken = googleUser.getAuthResponse().id_token;
			if (this.isEmailRegularGmail(userEmail) && this.isGmailWorkspaceRequiredByInstallable()) {
				this.openGmailWorkspaceSignupDialog(idToken);
			} else {
				await this.continueWithGmail(idToken);
			}
		},
		async continueWithGmail(idToken) {
			try {
				const ret = await this.loginWithGoogleToken({ token: idToken, isInstallable: true });
				if (!ret) {
					return;
				}
				// need to continue with signup, user was verified by google
				const { token } = ret;
				this.goToNextPage({ gtoken: token, isInstallable: true, installableId: this.installableId });
			} catch (err) {
				this.$toast.error(err);
			}
		},
		goToNextPage(params) {
			this.navigateTo({ name: 'UserDetailsForm', params });
		},
	},
	mounted() {
		this.localInstallableId = this.installableId || this.$route.query.installableId;
		UserRegistrationService.initializeGoogleAuth();
	},
	data() {
		return {
			email: '',
			auth2: null,
			isLoading: false,
			DOWNLOAD_CHROME_LINK,
			showEmailSignup: false,
			isPasswordValid: false,
			localInstallableId: null,
			isSignupFormValid: false,
			notSupportedBrowserEmail: '',
			showSuccessSubscription: false,
			isWrongGoogleEmailDialogOpen: false,
			emailRules: UserRegistrationService.getEmailValidationRules(),
			signupReferrer: UserRegistrationService.getSignupReferrer(document.referrer),
			installationSteps: [
				{ title: 'Create an account', iconName: 'installation-create-account' },
				{ title: 'Install the extension', iconName: 'installation-install-extension' },
				{ title: 'Add features to your apps', iconName: 'installation-add-features' },
			],
		};
	},
};
</script>

<style scoped lang="scss">
.send-email-container {
	width: 300px;
	::v-deep {
		.v-label {
			top: 8px !important;
		}
		.v-label--active {
			top: 16px !important;
		}
	}
}
.user-registration-layout {
	width: 100vw;
	overflow-y: auto;
	background: var(--page-background);
	background-image: url('../assets/png/registration-background.png');
	height: calc(100vh - calc(100vh - 100%)); // this is hack for 100vh in mobile

	@media (max-width: 600px) {
		padding: 0 20px;
	}

	.content-container {
		min-height: 80%;
		max-height: 80%;
		padding: 40px 0px 42px 0px;
	}

	.title-text {
		font-size: 20px;
		font-weight: bold;
		color: var(--primary-font-color);
	}
	.title-description-text {
		font-size: 16px;
		color: var(--primary-font-color);
	}

	.header-container {
		height: 60px;
		padding: 8px 52px;
		background: var(--bg-color);
		border-bottom: 1px solid var(--border-color);

		.login-button {
			padding: 0px;
			height: 45px;
			width: 132px;
			background: transparent;
			border: 1px solid var(--border-color);

			font-size: 14px !important;
			color: #384252 !important;
		}
	}

	.conetnet-container {
		.slot-card {
			text-align: center;
			border-radius: 10px;
			background: var(--bg-color);
			padding: 38px 65px 28px 65px;
			box-shadow: 0px 3px 6px var(--shadow-color);
		}
	}

	.footer-container {
		height: 60px;
	}
}

.step-title-text {
	font-size: 14px;
	color: var(--signup-grey);
}
.bottom-section {
	font-size: 14px;
	color: var(--primary-font-color);
}
.card-container {
	width: 580px;
	border-radius: 10px;
	background: var(--bg-color);
	padding: 24px 140px 16px 140px;
	&.narrow-padding {
		padding: 24px 114px 16px 114px;
	}
	&.mobile-layout {
		padding: 4px;
		width: calc(100vw - 32px);
	}
	box-shadow: 0px 3px 6px var(--shadow-color);
	height: 100%;
}

.card-title {
	font-size: 16px;
	font-weight: 600;
	color: var(--primary-font-color);
}
.google-login-button {
	padding: 0px;
	width: 300px;
	height: 45px;
	background: var(--bold-border-color) !important;
	color: var(--bg-color);
	font-size: 14px !important;
}

.divider-text {
	font-size: 14px;
	color: #babdc4;
}
.divider {
	height: 1px;
	margin-top: 2px;
	background: #babdc4;
}

.login-button {
	height: 45px;
	padding: 0px;
	font-size: 14px !important;
	color: var(--bold-border-color);
	background: var(--bg-color) !important;
	border: 1px solid var(--bold-border-color);
}
.continue-with-email {
	cursor: pointer;
	font-size: 14px;
	color: var(--primary-color);
	text-decoration: underline;
}

.mini-title {
	font-size: 16px;
	font-weight: bold;
	color: var(--primary-font-color);
}
.mini-title-description {
	font-size: 14px;
	font-weight: 600;
	color: var(--primary-font-color);
}

.description-text {
	font-size: 12px;
	color: var(--primary-font-color);
}
.success-message-text {
	font-size: 13px;
	font-weight: 500;
	text-align: center;
	color: var(--primary-color);
}
.send-button {
	padding: 0px;
	color: var(--bg-color);
	height: 45px !important;
	font-size: 16px !important;
	border: 1px solid var(--bold-border-color);
	background: var(--bold-border-color) !important;
	&::v-deep {
		&.v-btn:not(.v-btn--rounded):not(.v-btn--tile) {
			border-radius: 8px !important;
		}
	}
}
.terms-of-service {
	font-size: 12px;
	color: var(--secondary-font-color);
}
</style>
